<template>
	<div>
		<v-btn min-width="0" text @click="showNotifications">
			<v-badge
				:content="unreadCount"
				:value="unreadCount > 0"
				color="red"
				overlap
				bordered
			>
				<v-icon>mdi-bell</v-icon>
			</v-badge>
		</v-btn>
		<v-dialog v-model="showDialog" max-width="800" persistent>
			<v-card
				class="notification-detail-card d-flex flex-column"
				style="height: 80vh"
			>
				<!-- Fixed Header -->
				<v-card-title class="notification-header flex-shrink-0 pb-2">
					<div class="d-flex align-center w-100">
						<span class="headline">Notifications</span>
						<v-spacer></v-spacer>
						<div class="d-flex align-center">
							<span v-if="unreadCount > 0" class="text-caption">
								{{ unreadCount }} unread
							</span>
						</div>
					</div>
				</v-card-title>
				<v-divider></v-divider>

				<!-- Select All Section -->
				<div v-if="notifications.length > 0" class="pa-3 pl-6">
					<v-checkbox
						v-model="selectAll"
						:indeterminate="isIndeterminate"
						@change="toggleSelectAll"
						class="mt-0 pt-0"
						hide-details
						label="Select All"
					></v-checkbox>
				</div>
				<v-divider v-if="notifications.length > 0"></v-divider>

				<!-- Scrollable Content -->
				<v-card-text
					class="notification-content flex-grow-1 overflow-y-auto pa-0"
				>
					<div v-if="notifications.length > 0" class="pa-4">
						<v-list class="notifications-list">
							<div
								v-for="(notification, index) in notifications"
								:key="index"
								class="mb-3"
							>
								<v-card
									elevation="3"
									class="notification-card"
									:class="{
										'unread-notification':
											!notification.IsRead,
									}"
								>
									<v-list-item
										class="notifications-list-item"
									>
										<v-list-item-action>
											<v-checkbox
												v-model="selectedNotifications"
												:value="notification.Id"
												color="primary"
												hide-details
											></v-checkbox>
										</v-list-item-action>
										<v-list-item-content class="ml-2">
											<v-list-item-title
												class="pb-1"
												:class="{
													'font-weight-bold':
														!notification.IsRead,
												}"
											>
												<template
													v-if="
														isExportFile(
															notification
														)
													"
												>
													<span
														class="export-file-row"
													>
														File
														<a
															href="javascript:void(0)"
															@click.prevent="
																exportNotificationFile(
																	notification
																)
															"
															class="export-file-link"
														>
															<v-icon
																color="primary"
																size="18"
																class="mr-2"
																>mdi-file-check</v-icon
															>
															<span
																class="export-file-title"
																>{{
																	getExportFileTitle(
																		notification
																	)
																}}</span
															>
														</a>
														ready for download.
													</span>
												</template>
												<template v-else>
													<v-icon
														color="primary"
														size="18"
														class="mr-2"
														>{{
															getNotificationIcon(
																notification.Type
															)
														}}</v-icon
													>
													{{ notification.Message }}
												</template>
											</v-list-item-title>
											<v-list-item-subtitle>{{
												moment(
													notification.CreatedAt
												).fromNow()
											}}</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-card>
							</div>

							<!-- Loading indicator for infinite scroll -->
							<div
								v-if="notificationsLoading"
								class="text-center pa-4"
							>
								<v-progress-circular
									indeterminate
									color="primary"
									size="32"
								></v-progress-circular>
								<div class="mt-2 text-body-2">
									Loading more notifications...
								</div>
							</div>

							<!-- Load more button as fallback -->
							<div
								v-else-if="notificationsHasMore"
								class="text-center pa-4"
								ref="loadMoreTrigger"
							>
								<v-btn
									color="primary"
									outlined
									@click="loadMoreNotifications"
									:disabled="notificationsLoading"
								>
									Load More Notifications
								</v-btn>
							</div>

							<!-- End of notifications indicator -->
							<div
								v-else-if="notifications.length > 0"
								class="text-center pa-4"
							>
								<div class="text-body-2 grey--text">
									No more notifications
								</div>
							</div>
						</v-list>
					</div>
					<div v-else class="pa-4 text-center">
						<v-icon size="64" color="grey lighten-2" class="mb-4"
							>mdi-bell-outline</v-icon
						>
						<div class="text-h6 grey--text">No notifications</div>
					</div>
				</v-card-text>

				<!-- Fixed Footer -->
				<v-divider></v-divider>
				<v-card-actions class="notification-footer flex-shrink-0">
					<v-btn
						color="success"
						text
						@click="onMarkSelectedAsRead"
						:disabled="
							notificationsLoading ||
							!hasSelectedNotifications ||
							selectedUnreadCount === 0
						"
					>
						<v-icon small class="mr-1"
							>mdi-email-open-outline</v-icon
						>
						<span
							v-if="
								hasSelectedNotifications &&
								selectedUnreadCount > 0
							"
						>
							Mark as Read ({{ selectedUnreadCount }})
						</span>
						<span v-else> Mark as Read </span>
					</v-btn>
					<v-btn
						color="error"
						text
						@click="onDeleteSelected"
						:disabled="
							notificationsLoading || !hasSelectedNotifications
						"
					>
						<v-icon small class="mr-1">mdi-delete</v-icon>
						<span v-if="hasSelectedNotifications">
							Delete ({{ selectedNotifications.length }})
						</span>
						<span v-else> Delete </span>
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn color="primary" text @click="showDialog = false">
						Close
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { ProjectContextGetters } from '../../../store/getters';
import { GeneralActions, ProjectContextActions } from '../../../store/actions';
import { notificationTypes } from '../../../enums/notificationType';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import exportDataService from '../../../services/data/dataExportService';

export default {
	name: 'Notifications',
	data() {
		return {
			showDialog: false,
			intersectionObserver: null,
			selectedNotifications: [],
			selectAll: false,
		};
	},
	computed: {
		...mapGetters({
			notifications: ProjectContextGetters.NOTIFICATIONS,
			notificationsLoading: ProjectContextGetters.NOTIFICATIONS_LOADING,
			notificationsHasMore: ProjectContextGetters.NOTIFICATIONS_HAS_MORE,
			unreadCount: ProjectContextGetters.NOTIFICATIONS_UNREAD_COUNT,
		}),
		unreadNotifications() {
			return this.notifications.filter((n) => !n.IsRead);
		},
		selectedUnreadCount() {
			return this.selectedNotifications.filter((id) => {
				const notification = this.notifications.find(
					(n) => n.Id === id
				);
				return notification && !notification.IsRead;
			}).length;
		},
		selectedReadCount() {
			return this.selectedNotifications.filter((id) => {
				const notification = this.notifications.find(
					(n) => n.Id === id
				);
				return notification && notification.IsRead;
			}).length;
		},
		hasSelectedNotifications() {
			return this.selectedNotifications.length > 0;
		},
		hasSelectedUnreadNotifications() {
			return this.selectedUnreadCount > 0;
		},
		isIndeterminate() {
			return (
				this.selectedNotifications.length > 0 &&
				this.selectedNotifications.length < this.notifications.length
			);
		},
	},
	watch: {
		notificationsHasMore: {
			handler(newVal) {
				if (this.showDialog && newVal && !this.intersectionObserver) {
					this.$nextTick(() => this.setupIntersectionObserver());
				}
			},
		},
		notifications: {
			handler() {
				if (this.showDialog && this.notificationsHasMore) {
					this.$nextTick(() => {
						if (!this.intersectionObserver) {
							this.setupIntersectionObserver();
						}
					});
				}
				// Clear selection when notifications change
				this.selectedNotifications = [];
				this.selectAll = false;
			},
		},
		selectedNotifications: {
			handler(newSelection) {
				// Update selectAll state based on selection
				if (newSelection.length === 0) {
					this.selectAll = false;
				} else if (
					newSelection.length === this.notifications.length &&
					this.notifications.length > 0
				) {
					this.selectAll = true;
				}
			},
		},
	},
	methods: {
		showNotifications() {
			this.showDialog = !this.showDialog;
			if (this.showDialog) {
				this.$nextTick(() => {
					this.setupIntersectionObserver();
				});
			} else {
				this.destroyIntersectionObserver();
			}
		},

		setupIntersectionObserver() {
			if (!this.intersectionObserver && window.IntersectionObserver) {
				this.intersectionObserver = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							if (
								entry.isIntersecting &&
								this.notificationsHasMore &&
								!this.notificationsLoading
							) {
								this.loadMoreNotifications();
							}
						});
					},
					{
						root: null,
						rootMargin: '100px',
						threshold: 0.1,
					}
				);

				// Observe the trigger element - try multiple times to find it
				const tryObserve = (attempts = 0) => {
					const trigger = this.$refs.loadMoreTrigger;
					if (trigger) {
						this.intersectionObserver.observe(trigger);
					} else if (attempts < 5) {
						// Retry after a short delay
						setTimeout(() => tryObserve(attempts + 1), 100);
					}
				};

				this.$nextTick(() => tryObserve());
			}
		},

		destroyIntersectionObserver() {
			if (this.intersectionObserver) {
				this.intersectionObserver.disconnect();
				this.intersectionObserver = null;
			}
		},

		loadMoreNotifications() {
			return this.$store.dispatch(
				ProjectContextActions.LOAD_MORE_NOTIFICATIONS
			);
		},
		moment(date) {
			return moment(date);
		},

		onMarkAllAsRead() {
			ConfirmDialog({
				message:
					'Are you sure you want to mark all notifications as read?',
			}).then((isConfirmed) => {
				if (isConfirmed) {
					this.$store.dispatch(
						ProjectContextActions.MARK_ALL_NOTIFICATIONS_AS_READ
					);
				}
			});
		},
		getNotificationIcon(type) {
			switch (type) {
				case notificationTypes.ExportFile:
					return 'mdi-file-export-outline';
				case notificationTypes.ExportFileFailed:
					return 'mdi-file-alert-outline';
				case notificationTypes.ExportFileStarted:
					return 'mdi-file-sync-outline';
				case notificationTypes.ImportFile:
					return 'mdi-file-import-outline';
				case notificationTypes.ImportFileFailed:
					return 'mdi-file-remove-outline';
				case notificationTypes.ExportFileSuccess:
					return 'mdi-file-check';
				case 'success':
					return 'mdi-check-circle';
				case 'info':
					return 'mdi-information';
				default:
					return 'mdi-bell-outline';
			}
		},
		isExportFile(notification) {
			return notification.Type === notificationTypes.ExportFile;
		},
		getExportFileId(notification) {
			// Extract fileId from format (fileId,Filename)
			if (!notification.Message) return '';
			// Try to extract using regex - pattern to match (fileId,Filename)
			const match = notification.Message.match(/\(([^,]+),(.+?)\)/);
			if (match && match[1]) {
				// match[1] contains just the fileId portion
				return match[1].trim();
			}
			return notification.Message;
		},
		getExportFileTitle(notification) {
			// Extract filename from format (fileId,Filename)
			if (!notification.Message) return '';

			// Try to extract using regex
			const match = notification.Message.match(/\([^,]+,(.+?)\)/);
			if (match && match[1]) {
				return match[1].trim();
			}

			// Fallback to original message if parsing fails
			return notification.Message;
		},

		exportNotificationFile(notification) {
			let fileId = this.getExportFileId(notification);
			let fileName = this.getExportFileTitle(notification);
			this.exportFile(fileId, fileName);
		},

		exportFile(fileId, fileName) {
			this.$store.dispatch(GeneralActions.LOADING, true);
			exportDataService
				.downloadFile(fileId)
				.then((response) => {
					if (response.status === 200) {
						const blob = new Blob([response.data], {
							type: response.headers['content-type'],
						});
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', fileName);
						document.body.appendChild(link);
						link.click();
					}
				})
				.finally(() => {
					this.$store.dispatch(GeneralActions.LOADING, false);
				});
		},

		toggleSelectAll() {
			if (this.selectAll) {
				// Select all notifications
				this.selectedNotifications = this.notifications.map(
					(n) => n.Id
				);
			} else {
				// Deselect all
				this.selectedNotifications = [];
			}
		},

		onMarkSelectedAsRead() {
			if (this.selectedNotifications.length === 0) return;

			const unreadSelectedIds = this.selectedNotifications.filter(
				(id) => {
					const notification = this.notifications.find(
						(n) => n.Id === id
					);
					return notification && !notification.IsRead;
				}
			);

			if (unreadSelectedIds.length === 0) return;

			ConfirmDialog({
				message: `Are you sure you want to mark ${
					unreadSelectedIds.length
				} selected notification${
					unreadSelectedIds.length > 1 ? 's' : ''
				} as read?`,
			}).then((isConfirmed) => {
				if (isConfirmed) {
					this.$store
						.dispatch(
							ProjectContextActions.MARK_SELECTED_NOTIFICATIONS_AS_READ,
							unreadSelectedIds
						)
						.then(() => {
							// Clear selection after marking as read
							this.selectedNotifications = [];
							this.selectAll = false;
						});
				}
			});
		},

		onDeleteSelected() {
			if (this.selectedNotifications.length === 0) return;

			ConfirmDialog({
				message: `Are you sure you want to delete ${
					this.selectedNotifications.length
				} selected notification${
					this.selectedNotifications.length > 1 ? 's' : ''
				}?`,
			}).then((isConfirmed) => {
				if (isConfirmed) {
					this.$store
						.dispatch(
							ProjectContextActions.DELETE_SELECTED_NOTIFICATIONS,
							this.selectedNotifications
						)
						.then(() => {
							// Clear selection after deleting
							this.selectedNotifications = [];
							this.selectAll = false;
						});
				}
			});
		},
	},

	beforeDestroy() {
		this.destroyIntersectionObserver();
	},
};
</script>

<style lang="scss" scoped>
.notification-detail-card {
	.notification-header {
		background-color: #f8f9fa;
		border-bottom: 1px solid #e9ecef;
	}

	.notification-content {
		max-height: none; // Remove any max-height restrictions

		&::-webkit-scrollbar {
			width: 8px;
		}

		&::-webkit-scrollbar-track {
			background: #f1f1f1;
			border-radius: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background: #c1c1c1;
			border-radius: 4px;

			&:hover {
				background: #a8a8a8;
			}
		}
	}

	.notification-footer {
		background-color: #f8f9fa;
		border-top: 1px solid #e9ecef;
	}
}

.notifications-menu {
	max-width: 20%;
}

.notifications-list {
	padding: 0;

	.notification-card {
		border-radius: 8px;
		transition: all 0.3s ease;

		&.unread-notification {
			border-left: 4px solid #2196f3;
			background-color: #f8f9ff;
		}
	}
}

.export-file-link {
	display: flex;
	align-items: center;
	max-width: 400px;
	overflow: hidden;
	text-decoration: none;
}
.export-file-title {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: inline-block;
	vertical-align: middle;
	max-width: 340px;
}

.export-file-row {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: nowrap;
}
</style>
